import type { Column, AiSkillDefinition } from '@/types'
import Dexie, { type EntityTable } from 'dexie'
import type { Document } from '@/store/parser'
import { mergeEvents, type DeckEvent } from '@/lib/events'
import { useNotify } from '@/composables/useNotify'
import { i18n } from '@/plugins/i18n'

class AppDatabase extends Dexie {
  columns!: EntityTable<Column, 'id'>
  documents!: EntityTable<Document, 'id'>
  aiSkills!: EntityTable<AiSkillDefinition, 'id'>
  events!: EntityTable<DeckEvent, 'id'>

  constructor() {
    super('db')
    this.version(1).stores({
      columns: 'id,order',
      documents: 'id,shortId',
      aiSkills: 'id,type'
    })
    this.version(2).stores({
      columns: 'id,order',
      documents: 'id,shortId',
      aiSkills: 'id,type',
      notebookItems: null
    })
    this.version(3)
      .stores({
        columns: 'id,order',
        documents: 'id,shortId',
        aiSkills: 'id,type',
        events: 'id,lastPublished'
      })
      .upgrade(async tx => {
        const docs = await tx.table<Document>('documents').toArray()
        await tx.table<DeckEvent>('events').bulkPut(mergeEvents([], docs))
      })
  }
}

const db = new AppDatabase()

// Un autre onglet tient une connexion sur une version antérieure et bloque
// la migration. On informe l'utilisateur (au lieu de laisser une transaction
// échouer silencieusement plus tard).
db.on('blocked', () => {
  useNotify().warning(i18n.global.t('errors.db-blocked'))
})

// L'utilisateur a ouvert un onglet plus récent qui demande une migration.
// On ferme proprement la connexion actuelle et on recharge.
db.on('versionchange', () => {
  db.close()
  useNotify().info(i18n.global.t('errors.db-version-change'))
  location.reload()
})

// Précharge avant que la DB soit considérée comme ouverte, pour que le premier
// rendu ait des données et que l'UI n'affiche pas un état vide en flash.
db.on('ready', () =>
  Promise.all([
    db.columns.orderBy('order').toArray(),
    db.documents.toArray(),
    db.events.toArray()
  ])
)

export default db
